/**
 * Settings → Email templates (merged Clara Vale surface).
 *
 * Three stacked sections backed by EmailContentTemplate.kind:
 *   1. Email templates  - kind=notification  (stock messages, no upload)
 *   2. Email packets    - kind=evidence_request (email + upload link)
 *   3. Letters          - kind=letter (a formal letter sent as a PDF)
 *
 * Both sections share the same hairline-card / clara-th treatment used
 * across the rest of Settings. Editor lives at /settings/email-templates/<id>.
 *
 * Design ref: ansh-docs/design/email-templates-merged.html.
 */

import { MergeFieldText } from '@/admin/components/communications/merge-field-text'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Eye, Plus } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { Separator } from '@/shared/components/ui/separator'

import { PreviewDialog } from './email-authoring/preview-dialog'
import { useEmailTemplateList } from './email-authoring/use-email-templates'
import type { TemplateListRow } from './email-authoring/types'

const NOTIFICATION_FILTERS: Array<{ tag?: string; label: string }> = [
  { label: 'All' },
  { tag: 'Notification', label: 'Notifications' },
  { tag: 'Transactional', label: 'Transactional' },
  { tag: 'Auth', label: 'Auth' },
]

const PACKET_FILTERS: Array<{ tag?: string; label: string }> = [
  { label: 'All' },
  { tag: 'Fault', label: 'Fault types' },
  { tag: 'Onboarding', label: 'Onboarding' },
  { tag: 'Transfers', label: 'Transfers' },
]

export function EmailTemplatesTab() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [notifTag, setNotifTag] = useState<string | undefined>(undefined)
  const [packetTag, setPacketTag] = useState<string | undefined>(undefined)
  const [previewing, setPreviewing] = useState<TemplateListRow | null>(null)

  const { data: notifs = [], isLoading: loadingNotifs } = useEmailTemplateList(
    'notification',
    notifTag,
  )
  const { data: packets = [], isLoading: loadingPackets } = useEmailTemplateList(
    'evidence_request',
    packetTag,
  )
  const { data: letters = [], isLoading: loadingLetters } =
    useEmailTemplateList('letter')

  return (
    <div className="pb-16">
      {/* Section 1 - stock notification templates */}
      <Section
        eyebrow="Section one"
        title="Email templates"
        subtitle="Stock messages the platform sends: confirmations, status changes, password resets."
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate('/settings/email-templates/new?kind=notification')
            }
          >
            <Plus size={14} />
            New template
          </Button>
        }
      >
        <FilterRow
          label="Type"
          filters={NOTIFICATION_FILTERS}
          active={notifTag}
          onChange={setNotifTag}
        />
        <TemplateTable
          rows={notifs}
          isLoading={loadingNotifs}
          emptyHint="No templates yet for this filter."
          onClickRow={(t) => navigate(`/settings/email-templates/${t.id}`)}
          onPreview={(t) => setPreviewing(t)}
        />
      </Section>

      <Separator className="my-14 bg-[var(--rule-strong)]" />

      {/* Section 2 - evidence packets */}
      <Section
        eyebrow="Section two"
        title="Email packets"
        subtitle="Emails with a tokenised upload link. Used to gather photos and documents from consumers, contractors, or third parties tied to a specific claim or registration."
        action={
          <Button
            onClick={() =>
              navigate('/settings/email-templates/new?kind=evidence_request')
            }
          >
            <Plus size={14} />
            New packet
          </Button>
        }
        afterSubtitle={
          <div className="flex flex-wrap gap-4 mt-3 text-[11.5px] text-[var(--ink-3)]">
            <LegendDot color="amber">Fault-specific</LegendDot>
            <LegendDot color="green">General</LegendDot>
            <LegendDot color="slate">System</LegendDot>
            <LegendDot dashed>Auto-derived from triggers</LegendDot>
          </div>
        }
      >
        <FilterRow
          label="Used for"
          filters={PACKET_FILTERS}
          active={packetTag}
          onChange={setPacketTag}
        />
        <TemplateTable
          rows={packets}
          isLoading={loadingPackets}
          emptyHint="No packets yet - create one to wire an upload step into a workflow."
          onClickRow={(t) => navigate(`/settings/email-templates/${t.id}`)}
          onPreview={(t) => setPreviewing(t)}
        />
      </Section>

      <Separator className="my-14 bg-[var(--rule-strong)]" />

      {/* Section 3 - letters */}
      <Section
        eyebrow={t('emailTemplates.letters.eyebrow', 'Section three')}
        title={t('emailTemplates.letters.title', 'Letters')}
        subtitle={t(
          'emailTemplates.letters.subtitle',
          "Formal letters sent as a PDF, from a claim's Communications card or by a trigger. A letter about a claim is also kept on the claim and can be posted.",
        )}
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/settings/email-templates/new?kind=letter')}
          >
            <Plus size={14} />
            {t('emailTemplates.letters.new', 'New letter')}
          </Button>
        }
      >
        <TemplateTable
          rows={letters}
          isLoading={loadingLetters}
          emptyHint={t(
            'emailTemplates.letters.empty',
            'No letters yet - create one to send it from a claim.',
          )}
          onClickRow={(row) => navigate(`/settings/email-templates/${row.id}`)}
          onPreview={(row) => setPreviewing(row)}
        />
      </Section>

      {previewing && (
        <PreviewDialog
          template={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  )
}

export default EmailTemplatesTab

// ─── Layout primitives ──────────────────────────────────────────

function Section({
  eyebrow,
  title,
  subtitle,
  afterSubtitle,
  action,
  children,
}: {
  eyebrow: string
  title: string
  subtitle: string
  afterSubtitle?: React.ReactNode
  action: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="mb-4">
      <header className="flex items-end justify-between gap-4 flex-wrap mb-5">
        <div className="max-w-[58ch]">
          <p className="clara-section-label mb-1.5">{eyebrow}</p>
          <h2 className="font-display font-light text-[28px] leading-[1.1] text-[var(--ink)] m-0">
            {title}
          </h2>
          <p className="mt-1.5 text-[13.5px] text-[var(--ink-2)]">{subtitle}</p>
          {afterSubtitle}
        </div>
        <div>{action}</div>
      </header>
      {children}
    </section>
  )
}

function FilterRow({
  label,
  filters,
  active,
  onChange,
}: {
  label: string
  filters: Array<{ tag?: string; label: string }>
  active: string | undefined
  onChange: (next: string | undefined) => void
}) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap mb-4">
      <span className="clara-label mr-1.5">{label}</span>
      {filters.map((f) => {
        const isActive = active === f.tag
        return (
          <button
            key={f.label}
            type="button"
            onClick={() => onChange(f.tag)}
            className={
              'rounded-full border-[0.5px] px-3 py-1 text-[12px] transition-colors ' +
              (isActive
                ? 'bg-[var(--ink)] text-[var(--bg-card)] border-[var(--ink)]'
                : 'bg-transparent text-[var(--ink-2)] border-[var(--rule-strong)] hover:bg-[var(--rule-light)]')
            }
          >
            {f.label}
          </button>
        )
      })}
    </div>
  )
}

function TemplateTable({
  rows,
  isLoading,
  emptyHint,
  onClickRow,
  onPreview,
}: {
  rows: TemplateListRow[]
  isLoading: boolean
  emptyHint: string
  onClickRow: (row: TemplateListRow) => void
  onPreview: (row: TemplateListRow) => void
}) {
  if (isLoading && rows.length === 0) {
    return (
      <div className="bg-[var(--bg-card)] border-[0.5px] border-[var(--rule-strong)] rounded-xl p-8 text-center text-[13px] text-[var(--ink-3)]">
        Loading…
      </div>
    )
  }
  if (!isLoading && rows.length === 0) {
    return (
      <div className="bg-[var(--bg-card)] border-[0.5px] border-[var(--rule-strong)] rounded-xl p-8 text-center text-[13px] text-[var(--ink-3)]">
        {emptyHint}
      </div>
    )
  }
  return (
    <div className="bg-[var(--bg-card)] border-[0.5px] border-[var(--rule-strong)] rounded-xl overflow-hidden">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="clara-th text-left px-5 py-3 border-b-[0.5px] border-b-[var(--rule-light)] w-[28%]">
              Template
            </th>
            <th className="clara-th text-left px-5 py-3 border-b-[0.5px] border-b-[var(--rule-light)] w-[34%]">
              Used for
            </th>
            <th className="clara-th text-left px-5 py-3 border-b-[0.5px] border-b-[var(--rule-light)] w-[14%]">
              Status
            </th>
            <th className="clara-th text-right px-5 py-3 border-b-[0.5px] border-b-[var(--rule-light)] w-[14%]">
              Updated
            </th>
            <th className="clara-th text-right px-5 py-3 border-b-[0.5px] border-b-[var(--rule-light)] w-[10%]">
              {/* preview action */}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TemplateRow
              key={row.id}
              row={row}
              onClick={() => onClickRow(row)}
              onPreview={() => onPreview(row)}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TemplateRow({
  row,
  onClick,
  onPreview,
}: {
  row: TemplateListRow
  onClick: () => void
  onPreview: () => void
}) {
  return (
    <tr
      onClick={onClick}
      className="border-b-[0.5px] border-b-[var(--rule-light)] last:border-b-0 hover:bg-[rgba(26,26,24,0.02)] cursor-pointer"
    >
      <td className="px-5 py-4 align-top">
        <div
          className="text-[14px] font-normal text-[var(--ink)]"
          title={row.slug}
        >
          {row.name}
        </div>
        {row.subject && (
          <div className="text-[12px] text-[var(--ink-3)] mt-0.5 truncate max-w-[42ch]">
            <MergeFieldText text={row.subject} />
          </div>
        )}
      </td>
      <td className="px-5 py-4 align-top">
        <TagRow tags={row.tags ?? []} autoTags={row.auto_tags ?? []} />
      </td>
      <td className="px-5 py-4 align-top">
        <span
          className={
            'text-[11px] tracking-wider ' +
            (row.is_active ? 'text-[var(--green)]' : 'text-[var(--ink-3)]')
          }
        >
          {row.is_active ? 'Active' : 'Disabled'}
        </span>
      </td>
      <td className="px-5 py-4 align-top text-right text-[12px] text-[var(--ink-3)] whitespace-nowrap">
        {formatRelative(row.updated_at)}
      </td>
      <td className="px-5 py-4 align-top text-right">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onPreview()
          }}
          className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--rule-light)] transition-colors"
          aria-label="Preview"
          title="Preview email + PDF"
        >
          <Eye size={15} />
        </button>
      </td>
    </tr>
  )
}

// ─── Tag chips ──────────────────────────────────────────────────

function TagRow({ tags, autoTags }: { tags: string[]; autoTags: string[] }) {
  if (!tags.length && !autoTags.length) {
    return <span className="text-[12.5px] text-[var(--ink-3)]">-</span>
  }
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((t) => (
        <ColouredTag key={`a-${t}`} label={t} />
      ))}
      {autoTags.map((t) => (
        <span
          key={`d-${t}`}
          className="inline-flex items-center gap-1 text-[10.5px] tracking-wide px-2 py-0.5 rounded-full border-[0.5px] border-dashed border-[var(--rule-strong)] text-[var(--ink-3)] bg-transparent"
        >
          {t}
        </span>
      ))}
    </div>
  )
}

function ColouredTag({ label }: { label: string }) {
  const tone = pickTagTone(label)
  const palette: Record<string, string> = {
    fault:
      'bg-[rgba(184,115,42,0.08)] text-[var(--amber)] border-[rgba(184,115,42,0.25)]',
    green:
      'bg-[rgba(47,79,58,0.08)] text-[var(--green)] border-[rgba(47,79,58,0.25)]',
    slate:
      'bg-[rgba(90,100,112,0.08)] text-[var(--slate)] border-[rgba(90,100,112,0.25)]',
    neutral: 'bg-[var(--bg)] text-[var(--ink-2)] border-[var(--rule-strong)]',
  }
  return (
    <span
      className={
        'inline-flex items-center gap-1 text-[10.5px] tracking-wide px-2 py-0.5 rounded-full border-[0.5px] ' +
        palette[tone]
      }
    >
      {label}
    </span>
  )
}

function pickTagTone(label: string): 'fault' | 'green' | 'slate' | 'neutral' {
  const lower = label.toLowerCase()
  if (lower.startsWith('fault')) return 'fault'
  if (lower.startsWith('auth') || lower.startsWith('system')) return 'slate'
  if (
    lower.startsWith('notification') ||
    lower.startsWith('onboarding') ||
    lower.startsWith('transfers') ||
    lower.startsWith('claims')
  )
    return 'green'
  return 'neutral'
}

// ─── Legend ────────────────────────────────────────────────────

function LegendDot({
  color,
  dashed,
  children,
}: {
  color?: 'amber' | 'green' | 'slate'
  dashed?: boolean
  children: React.ReactNode
}) {
  const dotPalette: Record<string, string> = {
    amber: 'bg-[rgba(184,115,42,0.4)]',
    green: 'bg-[rgba(47,79,58,0.4)]',
    slate: 'bg-[rgba(90,100,112,0.4)]',
  }
  const dotClass = dashed
    ? 'bg-transparent border-dashed'
    : color
      ? dotPalette[color]
      : ''
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={
          'inline-block w-2 h-2 rounded-full border-[0.5px] border-[var(--rule-strong)] ' +
          dotClass
        }
      />
      {children}
    </span>
  )
}

// ─── Helpers ───────────────────────────────────────────────────

function formatRelative(iso: string): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diff = Math.max(0, now - then)
  const day = 24 * 60 * 60 * 1000
  if (diff < day) return 'Today'
  if (diff < 2 * day) return 'Yesterday'
  if (diff < 30 * day) return `${Math.floor(diff / day)} days ago`
  if (diff < 365 * day) return `${Math.floor(diff / (30 * day))} mo ago`
  return `${Math.floor(diff / (365 * day))} yr ago`
}
